import { useEffect, useState } from "react";
import style from "./frameAspectRatioPicker.module.css";
import { DropdownButton, Icon } from "../toolbar-buttons";
import classNames from "classnames";
import { CimFrameIcons, FramePresets, AspectRatioPreset } from "frontend/data/frames/frames-presets";

export default function AspectRatioPicker({
  value,
  onChange,
  multi,
}: {
  value?: any;
  onChange?: any;
  multi?: boolean;
}) {
  function findSelectedPreset(): AspectRatioPreset {
    if (value.scaleX !== value.scaleY) {
      return FramePresets[0];
    }
    return FramePresets.find((p) => p.width === value.width && p.height === value.height) || FramePresets[0];
  }

  const [selectedPreset, setSelectedPreset] = useState(findSelectedPreset);
  useEffect(() => {
    setSelectedPreset(findSelectedPreset);
  }, [value.width, value.height, value.scaleX, value.scaleY]);

  // Since Konva's transformer only reacts to changes in scale and not width/height,
  // setting the scale to be 1 will cause the transformer not to re-render on undoing
  // a change between 2 presets, since only the width and height change. This is solved
  // by adding a random miniscule amount to the scale. In the future it's worth
  // implementing a proper forceUpdate for the transformer on undo.
  function randomizeScale(baseScale: number) {
    const randomNumber = Math.floor(Math.random() * 10000) / 10 ** 16;
    return baseScale + randomNumber;
  }

  function handlePresetSelection(preset: AspectRatioPreset) {
    if (preset.id === selectedPreset.id) {
      return;
    }
    if (!preset.width || !preset.height) {
      setSelectedPreset(preset);
      return;
    }
    const area = value.width * (value.scaleX ?? 1) * (value.height * (value.scaleY ?? 1));
    const presetArea = preset.width * preset.height;
    const scale = randomizeScale(Math.sqrt(area / presetArea));
    onChange && onChange({ width: preset.width, height: preset.height, scaleX: scale, scaleY: scale });
  }

  function renderAspectRatioPresets() {
    return (
      <>
        <div className={style.aspectRatioButtonsGrid}>
          {FramePresets.slice(0, FramePresets.length - 3).map((preset) => (
            <div
              key={preset.id}
              className={classNames(style.aspectRatioButton, preset.id === selectedPreset.id && style.selected)}
              onClick={() => handlePresetSelection(preset)}
              data-testid={"AspectRatioButton-" + preset.id}
            >
              <Icon icon={CimFrameIcons[preset.id]} />
              <p className={style.aspectRatioOptionLabel}>{preset.displayName}</p>
            </div>
          ))}
        </div>
        <div className={style.divider} />

        <div className={style.aspectRatioButtonsGrid}>
          {FramePresets.slice(FramePresets.length - 3).map((preset) => (
            <div
              key={preset.id}
              className={classNames(style.aspectRatioButton, preset.id === selectedPreset.id && style.selected)}
              onClick={() => handlePresetSelection(preset)}
              data-testid={"AspectRatioButton-" + preset.id}
            >
              <Icon icon={CimFrameIcons[preset.id]} />
              <p className={style.aspectRatioOptionLabel}>{preset.displayName}</p>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <DropdownButton
      tooltip="Custom ratio"
      renderPopup={renderAspectRatioPresets}
      data-testid="FrameAspectRatio"
      className={style.aspectRatioPopup}
      hideArrow
    >
      <Icon icon="icon-frame-aspect-ratio-open" />
      <p className={style.aspectRatioButtonLabel}>{multi ? "Multiple" : selectedPreset.displayName}</p>
    </DropdownButton>
  );
}
